"use strict";
/**
 * Feature Flag Evaluation Engine
 *
 * This is the main evaluation engine that computes flag values for a user.
 * It is designed to be:
 * - Pure: No side effects, all inputs are explicit
 * - DAG-aware: Handles flag dependencies correctly (from FlagProgram.inputs.flags)
 * - Deterministic: Same inputs always produce same outputs
 *
 * IMPORTANT: Dependencies are defined in the DATA layer (FlagProgram.inputs.flags),
 * not in the CODE layer (FlagDefinition). The engine uses FlagProgram for all
 * evaluation logic including defaults, rules, rollouts, and dependencies.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeRolloutBucket = computeRolloutBucket;
exports.evaluateAllFlags = evaluateAllFlags;
exports.evaluateFlagValues = evaluateFlagValues;
exports.evaluateSingleFlag = evaluateSingleFlag;
exports.createEngineConfig = createEngineConfig;
const conditions_1 = require("./conditions");
const dag_1 = require("./dag");
const segments_1 = require("./segments");
/**
 * Compute a deterministic hash for rollout bucketing
 * Uses a simple string hash that produces consistent results
 *
 * @param userUid - The user's unique identifier
 * @param flagKey - The flag key being evaluated
 * @returns A bucket number in the range [0, 99] (inclusive)
 *
 * Rollout semantics:
 * - The bucket is compared against rolloutPercentage using `bucket < rolloutPercentage`
 * - For rolloutPercentage = 0: No users are included (0 buckets match)
 * - For rolloutPercentage = 1: ~1% of users are included (bucket 0 matches)
 * - For rolloutPercentage = 50: ~50% of users are included (buckets 0-49 match)
 * - For rolloutPercentage = 99: ~99% of users are included (buckets 0-98 match)
 * - For rolloutPercentage = 100: All users are included (buckets 0-99 match)
 */
function computeRolloutBucket(userUid, flagKey) {
    const input = `${userUid}:${flagKey}`;
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
        const char = input.charCodeAt(i);
        // Use multiplication instead of bitwise operations to avoid lint errors
        // This is equivalent to: hash = ((hash << 5) - hash + char) & 0xffffffff
        hash = Math.imul(31, hash) + char;
        // Keep within 32-bit integer range using modulo
        hash = hash % 0x100000000;
    }
    // Normalize to 0-99 range (100 possible buckets)
    return Math.abs(hash % 100);
}
/**
 * Evaluate a single targeting rule
 */
function evaluateRule(rule, context, resolvedFlags, segmentMemberships) {
    if (!rule.enabled) {
        return false;
    }
    return (0, conditions_1.evaluateCondition)(rule.condition, context, resolvedFlags, segmentMemberships);
}
/**
 * Evaluate a single flag using FlagProgram (DATA layer)
 *
 * @param flagKey - The flag key
 * @param definition - The flag definition (CODE layer - type info only)
 * @param program - The flag program (DATA layer - evaluation logic)
 * @param context - User and system context
 * @param resolvedFlags - Already resolved flag values (for dependencies)
 * @param segmentMemberships - Segment membership lookup
 */
function evaluateFlag(flagKey, definition, program, context, resolvedFlags, segmentMemberships) {
    // If no program exists, we can't evaluate (no default value available)
    if (!program) {
        return {
            value: undefined,
            reason: "error",
            error: `No program found for flag "${flagKey}"`,
        };
    }
    // If flag is disabled in program, return default
    if (!program.enabled) {
        return {
            value: program.defaultValue,
            reason: "disabled",
        };
    }
    // Evaluate targeting rules in order (first match wins)
    for (const rule of program.rules) {
        if (evaluateRule(rule, context, resolvedFlags, segmentMemberships)) {
            return {
                value: rule.value,
                reason: "rule",
                matchedRuleId: rule.id,
            };
        }
    }
    // Check rollout percentage
    if (program.rolloutPercentage !== undefined && program.rolloutPercentage < 100) {
        const bucket = computeRolloutBucket(context.user.userUid, flagKey);
        if (bucket < program.rolloutPercentage) {
            return {
                value: program.rolloutValue ?? true,
                reason: "rollout",
            };
        }
        else {
            return {
                value: program.defaultValue,
                reason: "rolloutExcluded",
            };
        }
    }
    // Return program default
    return {
        value: program.defaultValue,
        reason: "default",
    };
    // Note: definition is kept for future type validation but not used for defaults
    void definition;
}
/**
 * Main evaluation function - evaluates all flags for a user
 *
 * @param config - Engine configuration with definitions, programs, and segments
 * @param context - User and system context for evaluation
 * @returns Evaluation results for all flags
 */
function evaluateAllFlags(config, context) {
    const startTime = Date.now();
    const results = {};
    // Validate the registry and programs
    const validation = (0, dag_1.validateRegistryAndPrograms)(config.flagDefinitions, config.flagPrograms);
    if (!validation.valid) {
        // Return error results for all flags if validation fails
        for (const flagKey of Object.keys(config.flagDefinitions)) {
            results[flagKey] = {
                flagKey,
                value: undefined,
                reason: "error",
                error: validation.cycle
                    ? `Cyclic dependency: ${validation.cycle.join(" -> ")}`
                    : `Missing dependencies: ${validation.missingDependencies.map((e) => e.message).join(", ")}`,
                evaluatedAt: startTime,
            };
        }
        return {
            flags: results,
            userContextVersion: context.user.contextVersion,
            systemContextVersion: context.system.configVersion,
            evaluatedAt: startTime,
            evaluationDurationMs: Date.now() - startTime,
        };
    }
    // Evaluate segments first
    const segmentMemberships = (0, segments_1.evaluateAllSegments)(config.segments, context, {});
    // Get topologically sorted flag order (using FlagProgram dependencies)
    const sortedFlags = (0, dag_1.topologicalSortPrograms)(config.flagPrograms, config.flagDefinitions);
    // Evaluate flags in dependency order
    const resolvedFlags = {};
    for (const flagKey of sortedFlags) {
        const definition = config.flagDefinitions[flagKey];
        if (!definition) {
            continue;
        }
        const flagProgram = config.flagPrograms[flagKey];
        try {
            const result = evaluateFlag(flagKey, definition, flagProgram, context, resolvedFlags, segmentMemberships);
            // Store resolved value for dependent flags
            resolvedFlags[flagKey] = result.value;
            // Build full result
            const matchedSegments = Object.entries(segmentMemberships)
                .filter(([, m]) => m.isMember)
                .map(([id]) => id);
            // Get dependency chain from FlagProgram.inputs.flags (DATA layer)
            const dependencyChain = flagProgram?.inputs.flags ?? [];
            results[flagKey] = {
                flagKey,
                value: result.value,
                reason: result.reason,
                matchedRuleId: result.matchedRuleId,
                matchedSegments: matchedSegments.length > 0 ? matchedSegments : undefined,
                dependencyChain: dependencyChain.length > 0 ? dependencyChain : undefined,
                error: result.error,
                evaluatedAt: startTime,
            };
        }
        catch (error) {
            // On error, return undefined value (no default available without program)
            const program = config.flagPrograms[flagKey];
            results[flagKey] = {
                flagKey,
                value: program?.defaultValue,
                reason: "error",
                error: error instanceof Error ? error.message : "Unknown error",
                evaluatedAt: startTime,
            };
        }
    }
    return {
        flags: results,
        userContextVersion: context.user.contextVersion,
        systemContextVersion: context.system.configVersion,
        evaluatedAt: startTime,
        evaluationDurationMs: Date.now() - startTime,
    };
}
/**
 * Evaluate all flags and return just the values (simplified API)
 */
function evaluateFlagValues(config, context) {
    const result = evaluateAllFlags(config, context);
    const values = {};
    for (const [key, evaluation] of Object.entries(result.flags)) {
        values[key] = evaluation.value;
    }
    return values;
}
/**
 * Evaluate a single flag by key
 */
function evaluateSingleFlag(flagKey, config, context) {
    // For a single flag, we still need to evaluate its dependencies
    // So we evaluate all flags and return the requested one
    const result = evaluateAllFlags(config, context);
    const flagResult = result.flags[flagKey];
    if (!flagResult) {
        return {
            flagKey,
            value: undefined,
            reason: "error",
            error: `Flag "${flagKey}" not found in registry`,
            evaluatedAt: Date.now(),
        };
    }
    return flagResult;
}
/**
 * Create an evaluation engine config from raw data
 */
function createEngineConfig(flagDefinitions, flagPrograms, segments, environment) {
    return {
        flagDefinitions,
        flagPrograms,
        segments,
        environment,
    };
}
//# sourceMappingURL=engine.js.map